import type { PointerEvent, PointerEventHandler } from "react";

/**
 * Is `el` inside a Radix menu that is on its way out — closed, but still
 * mounted for its exit animation?
 */
export function isInClosingMenu(el: Element): boolean {
  return el.closest('[role="menu"]')?.getAttribute("data-state") === "closed";
}

/**
 * Wraps a menu item's pointer handler so that, once its menu has closed, the
 * pointer no longer moves focus. Radix Menu focuses the hovered item on
 * pointermove and the menu itself on pointerleave, and keeps doing both through
 * the exit animation: the item shrinking out from under a still pointer (Chrome
 * fires the boundary events) or the hand moving on took focus back from
 * whatever the chosen item had just focused. "Add checklist" in the New issue
 * lost its empty composer that way — blurred, it put itself away.
 *
 * Radix runs its own handler only when ours left the event un-prevented
 * (composeEventHandlers), so preventing it here is the whole fix.
 */
export function guardClosingMenuPointer<T extends HTMLElement>(
  handler: PointerEventHandler<T> | undefined
): PointerEventHandler<T> {
  return (event: PointerEvent<T>) => {
    handler?.(event);
    if (isInClosingMenu(event.currentTarget)) event.preventDefault();
  };
}
